import React, { useState, useEffect, useCallback } from 'react';
import { _T, C } from '../../shared/theme';

const BASE = process.env.REACT_APP_API_URL || '/api';
const go = async (method, path, body) => {
  const opts = { method, headers: { 'Content-Type': 'application/json' } };
  if (body !== undefined) opts.body = JSON.stringify(body);
  const res = await fetch(`${BASE}${path}`, opts);
  return res.json();
};

const card = { background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 16, padding: '14px 16px', marginBottom: 12 };
const codeBlock = { background: '#0d1117', border: '1px solid #30363d', borderRadius: 10, padding: '12px 14px', fontFamily: 'monospace', fontSize: 12, color: '#58d68d', overflowX: 'auto', position: 'relative', marginTop: 8 };
const badge = (color) => ({ background: color + '22', color, border: `1px solid ${color}44`, borderRadius: 5, padding: '2px 7px', fontSize: 11, fontWeight: 700, marginRight: 8, flexShrink: 0 });
const METHOD_COLOR = { GET: C.teal, POST: '#4AC87A', PUT: C.orange, DELETE: C.red };

const ENDPOINTS = {
  customers: [
    {
      method: 'GET', path: '/api/businesses/{bizId}/customers',
      desc_nl: 'Haal alle klanten op voor dit bedrijf.',
      desc_en: 'Retrieve all customers for this business.',
      example: `// Response\n[\n  {\n    "id": 1,\n    "first_name": "Jan",\n    "last_name": "Jansen",\n    "email": "jan@example.com",\n    "phone": "+31612345678",\n    "segment": "vip",\n    "tags": ["gym", "premium"]\n  }\n]`,
    },
    {
      method: 'GET', path: '/api/businesses/{bizId}/customers/{id}',
      desc_nl: 'Haal details op van één klant inclusief boekingshistorie.',
      desc_en: 'Retrieve details of a single customer including booking history.',
      example: `// Response\n{\n  "id": 1,\n  "first_name": "Jan",\n  "last_name": "Jansen",\n  "email": "jan@example.com",\n  "created_at": "2026-01-15T10:00:00Z"\n}`,
    },
    {
      method: 'POST', path: '/api/businesses/{bizId}/customers',
      desc_nl: 'Maak een nieuwe klant aan.',
      desc_en: 'Create a new customer.',
      example: `// Request body\n{\n  "first_name": "Jan",\n  "last_name": "Jansen",\n  "email": "jan@example.com",\n  "phone": "+31612345678",\n  "segment": "consumer",\n  "tags": ["gym"]\n}\n\n// Response\n{ "id": 1, "status": "created" }`,
    },
    {
      method: 'PUT', path: '/api/businesses/{bizId}/customers/{id}',
      desc_nl: 'Werk klantgegevens bij.',
      desc_en: 'Update customer data.',
      example: `// Request body\n{\n  "phone": "+31687654321",\n  "segment": "vip"\n}\n\n// Response\n{ "id": 1, "updated": true }`,
    },
    {
      method: 'DELETE', path: '/api/businesses/{bizId}/customers/{id}',
      desc_nl: 'Verwijder een klant (onomkeerbaar).',
      desc_en: 'Delete a customer (irreversible).',
      example: `// Response\n{ "deleted": true }`,
    },
  ],
  products: [
    {
      method: 'GET', path: '/api/businesses/{bizId}/products',
      desc_nl: 'Haal alle actieve en gearchiveerde producten op voor dit bedrijf, gesorteerd op naam.',
      desc_en: 'Retrieve all products for this business, sorted by name.',
      example: `// Response\n[\n  {\n    "id": 1,\n    "business_id": "abc123",\n    "name": "Fitness Shirt",\n    "sku": "FS-001",\n    "category": "clothing",\n    "description": "High-performance training shirt.",\n    "price": 34.99,\n    "sale_price": 24.99,\n    "currency": "EUR",\n    "vat_pct": 21,\n    "stock_qty": 50,\n    "low_stock_alert": 5,\n    "images": ["https://cdn.example.com/img1.jpg"],\n    "image_views": ["front", "back"],\n    "clothing_meta": { "sizes": ["S","M","L","XL"], "colors": ["black","white"] },\n    "distributor_name": "SportWholesale BV",\n    "distributor_contact": "orders@sportwholesale.nl",\n    "distributor_lead_days": 3,\n    "discount_type": "pct",\n    "discount_value": 0,\n    "status": "active",\n    "barcode": "8712345678901",\n    "weight_g": 180,\n    "tags": "fitness,training,shirt",\n    "homepage_published": 1,\n    "primary_image": 0,\n    "created_at": "2026-01-15T10:00:00Z",\n    "updated_at": "2026-03-01T14:22:00Z"\n  }\n]`,
    },
    {
      method: 'GET', path: '/api/businesses/{bizId}/products/{id}',
      desc_nl: 'Haal details op van één product op basis van ID.',
      desc_en: 'Retrieve full details of a single product by ID.',
      example: `// Response\n{\n  "id": 1,\n  "business_id": "abc123",\n  "name": "Fitness Shirt",\n  "sku": "FS-001",\n  "category": "clothing",\n  "description": "High-performance training shirt.",\n  "price": 34.99,\n  "sale_price": 24.99,\n  "currency": "EUR",\n  "vat_pct": 21,\n  "stock_qty": 50,\n  "low_stock_alert": 5,\n  "images": ["https://cdn.example.com/img1.jpg"],\n  "image_views": ["front", "back"],\n  "clothing_meta": { "sizes": ["S","M","L","XL"], "colors": ["black","white"] },\n  "distributor_name": "SportWholesale BV",\n  "distributor_contact": "orders@sportwholesale.nl",\n  "distributor_lead_days": 3,\n  "discount_type": "pct",\n  "discount_value": 0,\n  "status": "active",\n  "barcode": "8712345678901",\n  "weight_g": 180,\n  "tags": "fitness,training,shirt",\n  "homepage_published": 1,\n  "primary_image": 0,\n  "created_at": "2026-01-15T10:00:00Z",\n  "updated_at": "2026-03-01T14:22:00Z"\n}`,
    },
    {
      method: 'POST', path: '/api/businesses/{bizId}/products',
      desc_nl: 'Maak een nieuw product aan. Alleen "name" is verplicht.',
      desc_en: 'Create a new product. Only "name" is required.',
      example: `// Request body\n{\n  "name": "Fitness Shirt",               // required\n  "sku": "FS-001",                       // optional – stock keeping unit\n  "category": "clothing",               // optional – default: "general"\n  "description": "Training shirt.",     // optional\n  "price": 34.99,                       // optional – excl. VAT\n  "sale_price": 24.99,                  // optional – sale price excl. VAT (null to clear)\n  "currency": "EUR",                    // optional – default: "EUR"\n  "vat_pct": 21,                        // optional – default: 21\n  "stock_qty": 50,                      // optional – default: 0\n  "low_stock_alert": 5,                 // optional – alert threshold, default: 5\n  "images": ["https://cdn.example.com/img1.jpg"],  // optional – array of image URLs\n  "image_views": ["front", "back"],     // optional – label per image slot\n  "clothing_meta": {                    // optional – for clothing products\n    "sizes": ["S","M","L","XL"],\n    "colors": ["black","white","navy"]\n  },\n  "distributor_name": "SportWholesale BV",     // optional\n  "distributor_contact": "orders@sw.nl",       // optional – email or phone\n  "distributor_lead_days": 3,                  // optional – restock lead time in days\n  "discount_type": "pct",              // optional – "pct" | "fixed", default: "pct"\n  "discount_value": 0,                 // optional – discount amount, default: 0\n  "status": "active",                  // optional – "active" | "draft" | "archived"\n  "barcode": "8712345678901",          // optional – EAN/UPC barcode\n  "weight_g": 180,                     // optional – weight in grams\n  "tags": "fitness,training,shirt",    // optional – comma-separated tags\n  "homepage_published": 1,             // optional – 1 = show on homepage, default: 0\n  "primary_image": 0                   // optional – index of primary image in images[]\n}\n\n// Response\n{ "id": 2, "ok": true }`,
    },
    {
      method: 'PUT', path: '/api/businesses/{bizId}/products/{id}',
      desc_nl: 'Werk een product volledig bij. Stuur alle velden die je wil opslaan mee.',
      desc_en: 'Fully update a product. Send all fields you want to persist.',
      example: `// Request body (all fields accepted, same as POST)\n{\n  "name": "Fitness Shirt Pro",\n  "sku": "FS-001-PRO",\n  "category": "clothing",\n  "description": "Updated description.",\n  "price": 39.99,\n  "sale_price": null,                  // pass null to remove sale price\n  "currency": "EUR",\n  "vat_pct": 21,\n  "stock_qty": 30,\n  "low_stock_alert": 10,\n  "images": ["https://cdn.example.com/img1.jpg", "https://cdn.example.com/img2.jpg"],\n  "image_views": ["front", "back"],\n  "clothing_meta": {\n    "sizes": ["M","L","XL","XXL"],\n    "colors": ["black","white"]\n  },\n  "distributor_name": "SportWholesale BV",\n  "distributor_contact": "orders@sportwholesale.nl",\n  "distributor_lead_days": 5,\n  "discount_type": "fixed",\n  "discount_value": 5.00,\n  "status": "active",\n  "barcode": "8712345678902",\n  "weight_g": 200,\n  "tags": "fitness,pro,shirt",\n  "homepage_published": 1,\n  "primary_image": 0\n}\n\n// Response\n{ "ok": true }`,
    },
    {
      method: 'DELETE', path: '/api/businesses/{bizId}/products/{id}',
      desc_nl: 'Verwijder een product permanent (onomkeerbaar).',
      desc_en: 'Permanently delete a product (irreversible).',
      example: `// Response\n{ "ok": true }`,
    },
  ],
  orders: [
    {
      method: 'GET', path: '/api/businesses/{bizId}/orders',
      desc_nl: 'Haal alle bestellingen op.',
      desc_en: 'Retrieve all orders.',
      example: `// Response\n[\n  { "id": 1, "order_ref": "ORD-2026-1234", "customer_name": "Jan Jansen", "total": 99.99, "status": "pending" }\n]`,
    },
    {
      method: 'GET', path: '/api/businesses/{bizId}/orders/{id}',
      desc_nl: 'Haal details op van één bestelling inclusief artikelen.',
      desc_en: 'Retrieve details of a single order including line items.',
      example: `// Response\n{\n  "id": 1,\n  "order_ref": "ORD-2026-1234",\n  "items": [{ "product_name": "Membership", "qty": 1, "unit_price": 49.99 }],\n  "subtotal": 49.99,\n  "vat_total": 10.50,\n  "total": 60.49\n}`,
    },
    {
      method: 'POST', path: '/api/businesses/{bizId}/orders',
      desc_nl: 'Maak een nieuwe bestelling aan.',
      desc_en: 'Create a new order.',
      example: `// Request body\n{\n  "customer_name": "Jan Jansen",\n  "customer_email": "jan@example.com",\n  "items": [{ "product_name": "Membership", "qty": 1, "unit_price": 49.99, "vat_pct": 21 }]\n}\n\n// Response\n{ "id": 1, "order_ref": "ORD-2026-5678", "status": "pending" }`,
    },
    {
      method: 'PUT', path: '/api/businesses/{bizId}/orders/{id}',
      desc_nl: 'Werk de status of gegevens van een bestelling bij.',
      desc_en: 'Update the status or data of an order.',
      example: `// Request body\n{\n  "status": "confirmed"\n}\n\n// Response\n{ "id": 1, "updated": true }`,
    },
    {
      method: 'DELETE', path: '/api/businesses/{bizId}/orders/{id}',
      desc_nl: 'Verwijder een bestelling.',
      desc_en: 'Delete an order.',
      example: `// Response\n{ "deleted": true }`,
    },
  ],
  services: [
    {
      method: 'GET', path: '/api/businesses/{bizId}/services',
      desc_nl: 'Lijst van alle aangeboden diensten.',
      desc_en: 'List all offered services.',
      example: `// Response\n[\n  { "id": 1, "name": "Personal Training", "duration_min": 60, "price": 75.00 }\n]`,
    },
    {
      method: 'GET', path: '/api/businesses/{bizId}/services/{id}/slots?date=YYYY-MM-DD',
      desc_nl: 'Beschikbare tijdsloten voor een dienst op een datum.',
      desc_en: 'Available time slots for a service on a given date.',
      example: `// Response\n{\n  "date": "2026-04-01",\n  "slots": ["09:00", "10:00", "11:00", "14:00"]\n}`,
    },
    {
      method: 'POST', path: '/api/businesses/{bizId}/services',
      desc_nl: 'Maak een nieuwe dienst aan.',
      desc_en: 'Create a new service.',
      example: `// Request body\n{\n  "name": "Personal Training",\n  "duration_min": 60,\n  "price": 75.00,\n  "currency": "EUR"\n}\n\n// Response\n{ "id": 2, "status": "created" }`,
    },
    {
      method: 'PUT', path: '/api/businesses/{bizId}/services/{id}',
      desc_nl: 'Werk een dienst bij.',
      desc_en: 'Update a service.',
      example: `// Request body\n{\n  "price": 80.00,\n  "duration_min": 75\n}\n\n// Response\n{ "id": 1, "updated": true }`,
    },
    {
      method: 'DELETE', path: '/api/businesses/{bizId}/services/{id}',
      desc_nl: 'Verwijder een dienst.',
      desc_en: 'Delete a service.',
      example: `// Response\n{ "deleted": true }`,
    },
  ],
  bookings: [
    {
      method: 'GET', path: '/api/businesses/{bizId}/bookings',
      desc_nl: 'Haal alle boekingen op.',
      desc_en: 'Retrieve all bookings.',
      example: `// Response\n[\n  { "id": 1, "booker_name": "Jan Jansen", "service_name": "Training", "status": "confirmed" }\n]`,
    },
    {
      method: 'GET', path: '/api/businesses/{bizId}/bookings/{id}',
      desc_nl: 'Haal details op van één boeking.',
      desc_en: 'Retrieve details of a single booking.',
      example: `// Response\n{\n  "id": 1,\n  "booker_name": "Jan Jansen",\n  "booker_email": "jan@example.com",\n  "service_id": 1,\n  "requested_at": "2026-04-01T09:00:00Z",\n  "status": "confirmed"\n}`,
    },
    {
      method: 'POST', path: '/api/businesses/{bizId}/bookings',
      desc_nl: 'Maak een boeking aan.',
      desc_en: 'Create a booking.',
      example: `// Request body\n{\n  "service_id": 1,\n  "booker_name": "Jan Jansen",\n  "booker_email": "jan@example.com",\n  "requested_at": "2026-04-01T09:00:00Z"\n}\n\n// Response\n{ "id": 2, "status": "pending" }`,
    },
    {
      method: 'PUT', path: '/api/businesses/{bizId}/bookings/{id}',
      desc_nl: 'Werk de status van een boeking bij.',
      desc_en: 'Update the status of a booking.',
      example: `// Request body\n{\n  "status": "confirmed"\n}\n\n// Response\n{ "id": 1, "updated": true }`,
    },
    {
      method: 'DELETE', path: '/api/businesses/{bizId}/bookings/{id}',
      desc_nl: 'Verwijder een boeking.',
      desc_en: 'Delete a booking.',
      example: `// Response\n{ "deleted": true }`,
    },
  ],
  wellness: [
    {
      method: 'GET', path: '/api/public/{bizId}/members/health',
      desc_nl: 'Haal real-time gezondheids- en welzijnsscores op van alle medewerkers die toestemming hebben gegeven voor datadeling. Alleen metrieken die door het bedrijf zijn ingeschakeld in de Health Sharing Config worden teruggegeven.',
      desc_en: 'Retrieve real-time health and wellness scores for all employees who have consented to data sharing. Only metrics enabled by the business in the Health Sharing Config are returned.',
      example: `// Response\n{\n  "total": 12,\n  "enabled_metrics": ["concentratie", "reactievermogen", "burnout_score"],\n  "updated_at": "2026-05-31T08:00:00Z",\n  "members": [\n    {\n      "user_id": "uid_abc123",\n      "name": "Anna de Vries",\n      "department": "Marketing",\n      "sharing_enabled": true,\n      "scores": {\n        "concentratie":    { "score": 18, "max": 25, "pct": 72, "trend": "+3" },\n        "reactievermogen": { "score": 0.32, "unit": "s", "pct": 78, "trend": "-0.04" },\n        "geheugen":        { "score": 82, "pct": 82 },\n        "cognitie":        { "score": 74, "pct": 74 },\n        "zelfbewustzijn":  { "score": 68, "pct": 68 },\n        "emotiereg":       { "score": 71, "pct": 71 },\n        "mentale_helderheid": { "score": 65, "pct": 65 },\n        "soc_aanwezigheid":{ "score": 77, "pct": 77 },\n        "empathie":        { "score": 81, "pct": 81 },\n        "expressie":       { "score": 70, "pct": 70 },\n        "samenwerking":    { "score": 84, "pct": 84 },\n        "aanwezigheid":    { "score": 91, "pct": 91 },\n        "leiderschap":     { "score": 62, "pct": 62 },\n        "consistentie":    { "score": 78, "pct": 78 },\n        "burnout_score":   { "risk": "low", "score": 82 }\n      },\n      "updated_at": "2026-05-31T08:14:22Z"\n    }\n  ]\n}`,
    },
    {
      method: 'GET', path: '/api/public/{bizId}/members/health/{userId}',
      desc_nl: 'Haal de gezondheidsscores op van één specifieke medewerker.',
      desc_en: 'Retrieve health scores for a single specific employee.',
      example: `// Response\n{\n  "user_id": "uid_abc123",\n  "name": "Anna de Vries",\n  "department": "Marketing",\n  "sharing_enabled": true,\n  "scores": {\n    "concentratie":    { "score": 18, "max": 25, "pct": 72, "trend": "+3" },\n    "reactievermogen": { "score": 0.32, "unit": "s", "pct": 78 },\n    "burnout_score":   { "risk": "low", "score": 82 }\n  },\n  "updated_at": "2026-05-31T08:14:22Z"\n}`,
    },
    {
      method: 'POST', path: '/api/public/{bizId}/members',
      desc_nl: 'Maak een nieuw Sense Lifting account aan dat direct als teamlid wordt gekoppeld aan dit bedrijf. De gebruiker ontvangt een uitnodigingslink per e-mail om het account te activeren.',
      desc_en: 'Create a new Sense Lifting account that is immediately linked as a team member of this business. The user receives an invite link by email to activate the account.',
      example: `// Request body\n{\n  "name": "Anna de Vries",               // required – full name\n  "email": "anna@example.com",           // required – must be unique\n  "role": "member",                      // optional – "member" | "manager" | "hr" (default: "member")\n  "department_id": null,                 // optional – ID from GET /departments\n  "send_invite": true,                   // optional – send activation email (default: true)\n  "locale": "nl"                         // optional – "nl" | "en" (default: "nl")\n}\n\n// Response 201\n{\n  "user_id": "uid_new456",\n  "status": "created",\n  "invite_token": "tok_abc123xyz",\n  "invite_url": "https://senselifting.com/?bizinvite=tok_abc123xyz",\n  "member": {\n    "user_id": "uid_new456",\n    "name": "Anna de Vries",\n    "email": "anna@example.com",\n    "role": "member",\n    "status": "pending",\n    "sharing_enabled": false\n  }\n}\n\n// Error 409 – email already exists\n{ "error": "email_taken", "message": "A Sense Lifting account with this email already exists." }\n\n// Error 422 – validation\n{ "error": "validation", "fields": { "email": "required", "name": "required" } }`,
    },
  ],
};

const WEBHOOK_PAYLOAD = `{\n  "event": "order.created",\n  "timestamp": "2026-04-01T09:00:00Z",\n  "business_id": "{bizId}",\n  "data": {\n    "order_id": "ord_abc123",\n    "customer_email": "user@example.com",\n    "product_id": "prod_1",\n    "status": "pending"\n  }\n}`;

function CopyBtn({ text, label_nl, label_en, nl }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button onClick={copy} style={{ background: copied ? '#4AC87A22' : _T.bg4 || '#ffffff12', border: `1px solid ${copied ? '#4AC87A55' : _T.cardBorderBot}`, borderRadius: 7, padding: '3px 10px', color: copied ? '#4AC87A' : C.silver, fontSize: 12, cursor: 'pointer' }}>
      {copied ? (nl ? 'Gekopieerd!' : 'Copied!') : (label_nl && nl ? label_nl : (label_en || 'Copy'))}
    </button>
  );
}

function EndpointBlock({ ep, nl, businessId }) {
  const path = businessId ? ep.path.replace(/\{bizId\}/g, businessId) : ep.path;
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4, marginBottom: 4 }}>
        <span style={badge(METHOD_COLOR[ep.method] || C.silver)}>{ep.method}</span>
        <code style={{ color: C.white, fontSize: 13, wordBreak: 'break-all' }}>{path}</code>
      </div>
      <p style={{ color: C.silver, fontSize: 13, margin: '0 0 4px 0' }}>{nl ? ep.desc_nl : ep.desc_en}</p>
      <div style={codeBlock}>
        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{ep.example}</pre>
        <div style={{ position: 'absolute', top: 8, right: 8 }}>
          <CopyBtn text={ep.example} nl={nl} />
        </div>
      </div>
    </div>
  );
}

const TABS = ['customers', 'products', 'orders', 'services', 'bookings', 'wellness'];
const TAB_LABEL = { customers: { nl: 'Klanten', en: 'Customers' }, products: { nl: 'Producten', en: 'Products' }, orders: { nl: 'Bestellingen', en: 'Orders' }, services: { nl: 'Diensten', en: 'Services' }, bookings: { nl: 'Boekingen', en: 'Bookings' }, wellness: { nl: 'Medewerkers & Wellness', en: 'Members & Wellness' } };

export default function BusinessApiScreen({ lang, businessId, role }) {
  const nl = lang === 'nl';
  const canEdit = role === 'owner' || role === 'admin';

  const [apiKey, setApiKey] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [loadingKey, setLoadingKey] = useState(true);
  const [regen, setRegen] = useState(false);

  const [docTab, setDocTab] = useState('customers');

  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookSaved, setWebhookSaved] = useState(false);
  const [savingWebhook, setSavingWebhook] = useState(false);

  const loadKey = useCallback(() => {
    setLoadingKey(true);
    go('GET', `/businesses/${businessId}/api-key`)
      .then(d => { setApiKey(d.api_key || ''); setWebhookUrl(d.webhook_url || ''); })
      .catch(() => {})
      .finally(() => setLoadingKey(false));
  }, [businessId]);

  useEffect(() => { loadKey(); }, [loadKey]);

  const handleRegen = async () => {
    if (!window.confirm(nl ? 'Weet je zeker dat je de API-sleutel wil opnieuw genereren? De oude sleutel stopt onmiddellijk met werken.' : 'Are you sure you want to regenerate the API key? The old key will stop working immediately.')) return;
    setRegen(true);
    const d = await go('POST', `/businesses/${businessId}/api-key`).catch(() => ({}));
    if (d.api_key) setApiKey(d.api_key);
    setRevealed(false);
    setRegen(false);
  };

  const handleSaveWebhook = async () => {
    setSavingWebhook(true);
    await go('PUT', `/businesses/${businessId}/api-key`, { webhook_url: webhookUrl }).catch(() => {});
    setSavingWebhook(false);
    setWebhookSaved(true);
    setTimeout(() => setWebhookSaved(false), 2000);
  };

  const maskedKey = apiKey ? `sl_live_${'•'.repeat(16)}` : '';
  const displayKey = revealed ? apiKey : maskedKey;

  return (
    <div style={{ padding: '0 0 40px 0', maxWidth: 760 }}>
      <h2 style={{ color: C.white, fontSize: 20, fontWeight: 700, marginBottom: 10 }}>
        {nl ? 'API & Integraties' : 'API & Integrations'}
      </h2>

      {/* ── Business ID banner ── */}
      <div style={{ background: '#0d1117', border: '1px solid #30363d', borderRadius: 12, padding: '10px 14px', marginBottom: 18, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ color: C.silver, fontSize: 12 }}>{nl ? 'Jouw Business ID:' : 'Your Business ID:'}</span>
        <code style={{ color: '#58d68d', fontSize: 14, fontWeight: 700, letterSpacing: '0.03em', flex: 1, wordBreak: 'break-all' }}>{businessId}</code>
        <CopyBtn text={String(businessId)} label_nl="Kopieer" label_en="Copy" nl={nl} />
      </div>

      {/* ── API Key ── */}
      <div style={card}>
        <div style={{ color: _T.accent, fontWeight: 700, fontSize: 15, marginBottom: 10 }}>
          {nl ? 'API-sleutel' : 'API Key'}
        </div>
        {loadingKey ? (
          <span style={{ color: C.silver, fontSize: 13 }}>{nl ? 'Laden…' : 'Loading…'}</span>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
              <code style={{ background: '#0d1117', border: '1px solid #30363d', borderRadius: 8, padding: '6px 12px', color: '#58d68d', fontSize: 13, flex: 1, minWidth: 200, letterSpacing: revealed ? 0 : 2 }}>
                {displayKey || (nl ? '(geen sleutel)' : '(no key)')}
              </code>
              <button onClick={() => setRevealed(r => !r)} style={{ background: 'none', border: `1px solid ${_T.cardBorderBot}`, borderRadius: 7, padding: '5px 10px', color: C.silver, fontSize: 13, cursor: 'pointer' }}>
                {revealed ? (nl ? 'Verberg' : 'Hide') : (nl ? 'Toon' : 'Show')}
              </button>
              {apiKey && <CopyBtn text={apiKey} label_nl="Kopieer" label_en="Copy" nl={nl} />}
              {canEdit && (
                <button onClick={handleRegen} disabled={regen} style={{ background: C.red + '18', border: `1px solid ${C.red}44`, borderRadius: 7, padding: '5px 12px', color: C.red, fontSize: 13, cursor: regen ? 'not-allowed' : 'pointer', opacity: regen ? 0.6 : 1 }}>
                  {regen ? (nl ? 'Genereren…' : 'Generating…') : (nl ? 'Opnieuw genereren' : 'Regenerate')}
                </button>
              )}
            </div>
            <div style={{ background: C.red + '12', border: `1px solid ${C.red}33`, borderRadius: 8, padding: '8px 12px', color: C.red, fontSize: 12 }}>
              {nl ? 'Houd je API-sleutel geheim. Stel deze nooit bloot in client-side code.' : 'Keep your API key secret. Never expose it in client-side code.'}
            </div>
          </>
        )}
      </div>

      {/* ── Endpoint Docs ── */}
      <div style={card}>
        <div style={{ color: _T.accent, fontWeight: 700, fontSize: 15, marginBottom: 12 }}>
          {nl ? 'Endpoint Documentatie' : 'Endpoint Documentation'}
        </div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
          {TABS.map(t => (
            <button key={t} onClick={() => setDocTab(t)} style={{ background: docTab === t ? _T.accent + '22' : 'none', border: `1px solid ${docTab === t ? _T.accent : _T.cardBorderBot}`, borderRadius: 8, padding: '5px 14px', color: docTab === t ? _T.accent : C.silver, fontSize: 13, cursor: 'pointer', fontWeight: docTab === t ? 700 : 400 }}>
              {nl ? TAB_LABEL[t].nl : TAB_LABEL[t].en}
            </button>
          ))}
        </div>
        {(ENDPOINTS[docTab] || []).map((ep, i) => <EndpointBlock key={i} ep={ep} nl={nl} businessId={businessId} />)}
      </div>

      {/* ── Webhooks ── */}
      <div style={card}>
        <div style={{ color: _T.accent, fontWeight: 700, fontSize: 15, marginBottom: 10 }}>Webhooks</div>
        <p style={{ color: C.silver, fontSize: 13, margin: '0 0 10px 0' }}>
          {nl ? 'Stel een URL in om real-time meldingen te ontvangen bij gebeurtenissen.' : 'Set a URL to receive real-time notifications on events.'}
        </p>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          <input
            value={webhookUrl}
            onChange={e => setWebhookUrl(e.target.value)}
            placeholder="https://yourapp.com/webhook"
            disabled={!canEdit}
            style={{ flex: 1, minWidth: 220, background: '#0d1117', border: `1px solid ${_T.cardBorderBot}`, borderRadius: 8, padding: '7px 12px', color: C.white, fontSize: 13 }}
          />
          {canEdit && (
            <button onClick={handleSaveWebhook} disabled={savingWebhook} style={{ background: _T.accent + '22', border: `1px solid ${_T.accent}55`, borderRadius: 8, padding: '7px 16px', color: _T.accent, fontSize: 13, cursor: savingWebhook ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
              {webhookSaved ? (nl ? 'Opgeslagen!' : 'Saved!') : savingWebhook ? (nl ? 'Opslaan…' : 'Saving…') : (nl ? 'Opslaan' : 'Save')}
            </button>
          )}
        </div>
        <div style={{ color: C.silver2, fontSize: 12, marginBottom: 8, fontWeight: 600 }}>{nl ? 'Ondersteunde events:' : 'Supported events:'}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
          {['customer.created', 'customer.updated', 'order.created', 'order.updated', 'booking.created', 'booking.confirmed'].map(ev => (
            <span key={ev} style={{ background: _T.accent + '18', border: `1px solid ${_T.accent}33`, borderRadius: 6, padding: '3px 10px', color: _T.accent, fontSize: 12, fontFamily: 'monospace' }}>{ev}</span>
          ))}
        </div>
        <div style={{ color: C.silver2, fontSize: 12, marginBottom: 4, fontWeight: 600 }}>{nl ? 'Payload formaat:' : 'Payload format:'}</div>
        <div style={codeBlock}>
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{WEBHOOK_PAYLOAD}</pre>
          <div style={{ position: 'absolute', top: 8, right: 8 }}>
            <CopyBtn text={WEBHOOK_PAYLOAD} nl={nl} />
          </div>
        </div>
      </div>

      {/* ── Rate limits & info ── */}
      <div style={card}>
        <div style={{ color: _T.accent, fontWeight: 700, fontSize: 15, marginBottom: 12 }}>
          {nl ? 'Limieten & Informatie' : 'Rate Limits & Info'}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10, marginBottom: 14 }}>
          {[
            { label_nl: 'Verzoeken per uur', label_en: 'Requests / hour', value: '1 000' },
            { label_nl: 'Basis-URL', label_en: 'Base URL', value: 'https://senselifting.com/api/public/{bizId}/' },
          ].map(item => (
            <div key={item.label_en} style={{ background: '#0d1117', border: '1px solid #30363d', borderRadius: 10, padding: '10px 14px' }}>
              <div style={{ color: C.silver, fontSize: 11, marginBottom: 4 }}>{nl ? item.label_nl : item.label_en}</div>
              <code style={{ color: '#58d68d', fontSize: 12 }}>{item.value}</code>
            </div>
          ))}
        </div>
        <div style={{ color: C.silver2, fontSize: 12, marginBottom: 6, fontWeight: 600 }}>{nl ? 'Authenticatie header:' : 'Authentication header:'}</div>
        <div style={codeBlock}>
          <pre style={{ margin: 0 }}>{'Authorization: Bearer {api_key}'}</pre>
          <div style={{ position: 'absolute', top: 8, right: 8 }}>
            <CopyBtn text="Authorization: Bearer {api_key}" nl={nl} />
          </div>
        </div>
      </div>
    </div>
  );
}
